/** Renders selected-item evidence with fixed actions, dialogs and navigation. */
import {ArrowBack, ArrowForward, EditOutlined, Star, StarBorder} from "@mui/icons-material";
import {
   Alert,
   Box,
   Button,
   Chip,
   CircularProgress,
   IconButton,
   Stack,
   Tooltip,
   Typography
} from "@mui/material";
import {useState} from "react";
import {Link as RouterLink} from "react-router-dom";
import {DataViewColumn} from "../../../components/dataview/DataViewColumns";
import {
   BrandItemProblemInput,
   useBrandItemUpdateMutation,
   useDashboardBrandItemDrilldownItemsLazyQuery
} from "../../../Queries";
import {BrandItemBoostDialog} from "../BrandItemBoostDialog";
import {BrandItemBoostChip} from "../BrandItemBoostChip";
import {BrandItemPagePath} from "../brandItem/BrandItemPage";
import {BrandItemRequestAction} from "../brandItem/BrandItemRequestAction";
import {CohortRow, localizedValue} from "./BrandItemCohort";
import {BrandItemTimeline} from "./BrandItemTimeline";
import {DashboardDrilldownItem} from "./useBrandItemCohortItems";

interface BrandItemDrilldownDetailProps {
   brandHouseId: string;
   row: CohortRow;
   columns: DataViewColumn[];
   item?: DashboardDrilldownItem;
   position: number;
   total: number;
   onPrevious: () => void;
   onNext: () => void;
   onUpdated: (item: DashboardDrilldownItem) => void;
   loading: boolean;
   busy: boolean;
   onBusyChange: (busy: boolean) => void;
}

/** Emphasizes each current price with its same-currency RRP as secondary context. */
const SellingPriceList = ({
   sellingPrices,
   beforePrices
}: {
   sellingPrices?: Array<{currency: string; value: number} | null> | null;
   beforePrices?: Array<{currency: string; value: number} | null> | null;
}) => {
   const current = (sellingPrices || []).filter((price): price is NonNullable<typeof price> => !!price);
   const rrpByCurrency = new Map(
      (beforePrices || [])
         .filter((price): price is NonNullable<typeof price> => !!price)
         .map(price => [price.currency, price.value] as const)
   );
   if (!current.length) {
      return null;
   }
   return <Box>
      <Typography variant="caption" color="text.secondary">Current selling price</Typography>
      {current.map(price => <Stack direction="row" spacing={1.5} alignItems="baseline" key={price.currency}>
         <Typography variant="h5">{price.value} {price.currency}</Typography>
         {rrpByCurrency.has(price.currency) && <Typography
            variant="body2"
            color="text.secondary"
            sx={{textDecoration: "line-through"}}
         >
            {rrpByCurrency.get(price.currency)} {price.currency}
         </Typography>}
      </Stack>)}
   </Box>;
};

/** Displays condition as a compact three-star score. */
const ConditionStars = ({condition}: {condition: number}) => {
   const filled = Math.max(0, Math.min(3, Math.round(condition)));
   return <Stack
      direction="row"
      spacing={0}
      alignItems="center"
      aria-label={`Condition ${filled} out of 3`}
   >
      {Array.from({length: 3}, (_value, index) => index < filled
         ? <Star key={index} aria-hidden="true" sx={{fontSize: 16, color: "warning.main"}}/>
         : <StarBorder key={index} aria-hidden="true" sx={{fontSize: 16, color: "text.disabled"}}/>)}
   </Stack>;
};

/** Shows product and cohort evidence below a fixed item-action toolbar. */
export const BrandItemDrilldownDetail = ({
   brandHouseId,
   row,
   item,
   position,
   total,
   onPrevious,
   onNext,
   onUpdated,
   loading,
   busy,
   onBusyChange
}: BrandItemDrilldownDetailProps) => {
   const [requestActionOpen, setRequestActionOpen] = useState(false);
   const [boostOpen, setBoostOpen] = useState(false);
   const [updateItem] = useBrandItemUpdateMutation();
   const [reloadItem] = useDashboardBrandItemDrilldownItemsLazyQuery({fetchPolicy: "network-only"});
   const resolved = item?.resolved;
   const locale = typeof navigator === "undefined" ? undefined : navigator.language;

   if (!item && loading) {
      return <Stack spacing={2} sx={{p: 3}} alignItems="center" justifyContent="center" minHeight={300}>
         <CircularProgress size={32}/>
         <Typography color="text.secondary">Loading item details…</Typography>
      </Stack>;
   }

   if (!item || !resolved) {
      return <Stack spacing={2} sx={{p: 3}}>
         <Alert severity="warning">Item details are unavailable. The item remains in the cohort snapshot.</Alert>
         <Button href={BrandItemPagePath(brandHouseId, row.id, false)} target="_blank" rel="noreferrer">
            Try the full editor
         </Button>
      </Stack>;
   }

   const name = localizedValue(resolved.nameLocalized, locale, `Item #${item.itemNr}`);
   const detailParts = [
      localizedValue(resolved.sizeLocalized, locale, ""),
      localizedValue(resolved.colorLocalized, locale, "")
   ].filter(Boolean);

   /** Appends a requested action and updates the selected cohort item in place. */
   const submitRequest = async (problem: BrandItemProblemInput): Promise<void> => {
      const problems = (resolved.problems || [])
         .filter((entry): entry is NonNullable<typeof entry> => !!entry)
         .map(entry => ({
            createdAt: entry.createdAt,
            description: entry.description,
            solved: entry.solved
         }));
      problems.push(problem);

      const result = await updateItem({
         variables: {
            brandHouseId,
            input: {_id: item._id, problems}
         },
         context: {skipRetry: true}
      });
      const updatedProblems = result.data?.BrandItemUpdate?.resolved?.problems;
      if (!updatedProblems) {
         throw new Error("The action was saved but updated item details were not returned");
      }
      onUpdated({
         ...item,
         resolved: {
            ...resolved,
            problems: updatedProblems
         }
      });
   };

   /** Reloads the selected item after the shared Boost dialog changes it. */
   const refreshItem = async (): Promise<void> => {
      const result = await reloadItem({variables: {ids: [item._id]}});
      const refreshed = (result.data?.BrandItems || []).find(
         (entry): entry is DashboardDrilldownItem => !!entry && entry._id === item._id
      );
      if (refreshed) {
         onUpdated(refreshed);
      }
   };

   return <Stack sx={{height: "100%", minHeight: 0}}>
      <Box sx={{minHeight: 64, borderBottom: 1, borderColor: "#e2e2e2", bgcolor: "background.paper", px: {xs: 2, md: 3}}}>
         <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{minHeight: 64, maxWidth: 920, width: "100%", mx: "auto"}}
         >
            <Stack spacing={0} sx={{flex: 1, minWidth: 0}}>
               <Stack direction="row" spacing={0.5} alignItems="center" sx={{minWidth: 0}}>
                  <Typography component="h2" variant="h6" noWrap title={name}>{name}</Typography>
                  <Tooltip title="Open editor">
                     <span>
                        <IconButton
                           component={RouterLink}
                           to={BrandItemPagePath(brandHouseId, row.id, false)}
                           disabled={busy}
                           size="small"
                           aria-label="Open editor"
                        >
                           <EditOutlined fontSize="small"/>
                        </IconButton>
                     </span>
                  </Tooltip>
               </Stack>
               <Stack direction="row" spacing={3} alignItems="center">
                  <Typography variant="caption" color="text.secondary">Item #{item.itemNr}</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                     <Typography variant="caption" color="text.secondary">Condition:</Typography>
                     <ConditionStars condition={resolved.condition}/>
                  </Stack>
               </Stack>
            </Stack>
            <Stack direction="row" spacing={1}>
               <Button
                  variant="outlined"
                  disabled={busy}
                  size="small"
                  onClick={() => {
                     setBoostOpen(true);
                     onBusyChange(true);
                  }}
               >
                  Boost
               </Button>
               <Button
                  variant="outlined"
                  disabled={busy}
                  size="small"
                  onClick={() => {
                     setRequestActionOpen(true);
                     onBusyChange(true);
                  }}
               >
                  Request action
               </Button>
            </Stack>
         </Stack>
      </Box>

      <Box sx={{flex: 1, minHeight: 0, overflowY: "auto"}}>
         <Stack spacing={3} sx={{p: {xs: 2, md: 3}, maxWidth: 920, width: "100%", mx: "auto"}}>
         <Stack
            direction={{xs: "column", sm: "row"}}
            spacing={3}
            alignItems={{sm: "flex-start"}}
         >
            <Box
               component="img"
               src={resolved.imageUrls?.[0]}
               alt=""
               sx={{
                  width: {xs: "100%", sm: 220},
                  height: {xs: 280, sm: 280},
                  objectFit: "contain",
                  bgcolor: "grey.50",
                  border: 1,
                  borderColor: "divider"
               }}
            />
            <Stack spacing={1.5} sx={{flex: 1, minWidth: 0}}>
               <Box>
                  <Typography variant="body2" color="text.secondary">{detailParts.join(" · ")}</Typography>
               </Box>
               <SellingPriceList
                  sellingPrices={resolved.sellingPrices}
                  beforePrices={resolved.beforePrices}
               />
               {resolved.activeBoost
                  ? <BrandItemBoostChip boost={resolved.activeBoost}/>
                  : <Chip size="small" label="Not boosted" variant="outlined" sx={{alignSelf: "flex-start"}}/>}
            </Stack>
         </Stack>

            <BrandItemTimeline brandItemId={row.id} brandHouseId={brandHouseId}/>
         </Stack>
      </Box>

      <Box sx={{borderTop: 1, borderColor: "#e2e2e2", bgcolor: "background.paper", px: {xs: 2, md: 3}, py: 1}}>
         <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{maxWidth: 920, width: "100%", mx: "auto"}}
         >
            <Button startIcon={<ArrowBack/>} disabled={busy || position <= 0} onClick={onPrevious}>
               Previous item
            </Button>
            <Typography variant="body2" color="text.secondary">{position + 1} of {total}</Typography>
            <Button endIcon={<ArrowForward/>} disabled={busy || position >= total - 1} onClick={onNext}>
               Next item
            </Button>
         </Stack>
      </Box>

      <BrandItemRequestAction
         open={requestActionOpen}
         onClose={() => {
            setRequestActionOpen(false);
            onBusyChange(false);
         }}
         onSubmitRequest={submitRequest}
      />
      {boostOpen && <BrandItemBoostDialog
         brandItemIds={[item._id]}
         open={boostOpen}
         onClose={() => {
            setBoostOpen(false);
            onBusyChange(false);
         }}
         onCompleted={() => void refreshItem()}
      />}
   </Stack>;
};
